import { type FormikHandlers } from 'formik'
import * as Yup from 'yup'

import { Select, Textarea } from '@loadsmart/loadsmart-ui'
import { Drawer, Field, Icon, LabeledValue, Layout, Text } from '@loadsmart/miranda-react'

import { isAppointmentResetable } from 'core/appointments/domain/Appointment'

import { type OpenDockStopAppointment } from '../../domain/Shipment'
import { type AppointmentCommonFormValues } from '../../infra/ShipmentRepository'
import { APPOINTMENT_MISSCHEDULING_REASON_OPTIONS } from '../../presenters/appointment'
import { OpenDockAppointmentSummary } from './OpenDockAppointmentSummary'

export function getValidationSchema() {
  return Yup.object().shape({
    misschedulingReason: Yup.object().shape({
      label: Yup.string().required(),
      value: Yup.string().required(),
    }),
    misschedulingNote: Yup.string()
      .nullable()
      .max(100, 'Note must be 100 characters or less')
      .when('misschedulingReason.value', {
        is: 'other',
        then: () => Yup.string().required('Must provide a note'),
      }),
  })
}

export function Scheduled({
  onReschedule,
  onCancelAppointment,
  stopAppointment,
  facilityName,
  rescheduleByCarrierForbidden = false,
  facilityTimezone,
  values,
  handleChange,
  isValid,
  dirty,
  isSubmitting,
}: {
  onReschedule: () => void
  onCancelAppointment: () => void
  stopAppointment?: OpenDockStopAppointment
  facilityName?: string
  facilityTimezone?: string
  rescheduleByCarrierForbidden: boolean
  values: AppointmentCommonFormValues
  handleChange: FormikHandlers['handleChange']
  isValid: boolean
  dirty: boolean
  isSubmitting: boolean
}) {
  const isResetableStatus = isAppointmentResetable(stopAppointment)
  const disableSubmit =
    !isValid || isSubmitting || !dirty || rescheduleByCarrierForbidden || isResetableStatus

  return (
    <>
      <Drawer.Header>
        <Layout.Group align="center" gap="spacing-1">
          <Icon name="calendar" size="24px" />
          Scheduled
        </Layout.Group>
        <Drawer.Close />
      </Drawer.Header>
      <Drawer.Body>
        <Layout.Stack gap="spacing-4" paddingB="spacing-2">
          <OpenDockAppointmentSummary
            facilityName={facilityName}
            facilityTimezone={facilityTimezone}
            stopAppointment={stopAppointment}
            betweenLoadTypeAndScheduledAt={
              <LabeledValue>
                <LabeledValue.Label>Docks</LabeledValue.Label>
                <LabeledValue.Value>{stopAppointment?.dockName ?? '-'}</LabeledValue.Value>
              </LabeledValue>
            }
          />
          <Field label="Reschedule reason" size="small" required>
            <Select
              name="misschedulingReason"
              placeholder="Select reason"
              options={APPOINTMENT_MISSCHEDULING_REASON_OPTIONS}
              value={values.misschedulingReason}
              onChange={handleChange}
              hideClear
              disabled={rescheduleByCarrierForbidden || isResetableStatus}
            />
          </Field>
          <Field
            style={{ flex: 1 }}
            label="Note (max 100 characters)"
            required={values.misschedulingReason?.value === 'other'}
          >
            <Textarea
              onInput={handleChange}
              name="misschedulingNote"
              value={values.misschedulingNote ?? ''}
              aria-label="Note"
              maxLength={100}
              disabled={rescheduleByCarrierForbidden || isResetableStatus}
            />
          </Field>
          {rescheduleByCarrierForbidden && (
            <Layout.Box
              paddingY="spacing-4"
              paddingX="spacing-6"
              borderWidth="border-thin"
              borderRadius="border-radius-m"
              borderColor="color-border-input"
            >
              <Layout.Stack gap="spacing-1">
                <Text variant="body-md-bold">
                  This facility does not allow carriers to reschedule appointments.
                </Text>
                <Text variant="body-sm" color="color-text-tertiary">
                  Please contact the shipper or warehouse directly for any changes.
                </Text>
              </Layout.Stack>
            </Layout.Box>
          )}
          {isResetableStatus && (
            <Layout.Box
              paddingY="spacing-4"
              paddingX="spacing-6"
              borderWidth="border-thin"
              borderRadius="border-radius-m"
              borderColor="color-border-input"
            >
              <Layout.Stack gap="spacing-1">
                <Text variant="body-md-bold">This appointment has been canceled.</Text>
                <Text variant="body-sm" color="color-text-tertiary">
                  Click <strong>Cancel appointment</strong> and then <strong>Reset</strong> to start
                  a new scheduling request.
                </Text>
              </Layout.Stack>
            </Layout.Box>
          )}
        </Layout.Stack>
      </Drawer.Body>
      <Drawer.Actions>
        <Drawer.ActionTertiary style={{ marginRight: 'auto' }} onClick={onCancelAppointment}>
          Cancel appointment
        </Drawer.ActionTertiary>
        <Drawer.ActionPrimary disabled={disableSubmit} onClick={onReschedule}>
          Reschedule
        </Drawer.ActionPrimary>
      </Drawer.Actions>
    </>
  )
}
